import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bookKey, getBook, updateBook } from "../api";
import { useT } from "../i18n";

/**
 * BookStory is whether a book tells a story (ADR-0158, requirement 0092).
 *
 * The author says; nothing guesses. The answer follows it, reading an argument
 * as an argument rather than as plot, and a book that tells no story is always
 * asked openly (ADR-0160). Not part of an edition, like free: it reaches readers
 * without a publish.
 */
export default function BookStory({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const choose = useMutation({
    mutationFn: (story: boolean) => updateBook(bookID, { story }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["book", bookID] }),
  });

  // Every book is a story until its author says otherwise: an older server
  // that sends no field means a story too.
  const story = book.data?.story ?? true;

  return (
    <div data-testid="book-story">
      <p className="block text-sm font-medium">{t.book.story}</p>
      <p className="mt-1 mb-2 text-xs text-muted">{t.book.storyNote}</p>
      <div className="flex flex-wrap gap-2">
        {[
          { value: true, label: t.book.storyYes, testID: "book-story-yes" },
          { value: false, label: t.book.storyNo, testID: "book-story-no" },
        ].map((choice) => (
          <button
            key={String(choice.value)}
            type="button"
            data-testid={choice.testID}
            aria-pressed={story === choice.value}
            disabled={choose.isPending || book.isPending}
            onClick={() => {
              if (choice.value !== story) choose.mutate(choice.value);
            }}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${
              story === choice.value
                ? "border-sage bg-sage-soft font-medium"
                : "border-line hover:bg-sunk"
            }`}
          >
            {choice.label}
          </button>
        ))}
      </div>
      {choose.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.book.storyFailed}
        </p>
      )}
    </div>
  );
}
